import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { computed, DestroyRef, inject, Injectable, signal, Type } from '@angular/core';

import { LoggerService } from '@spaces-ui/logger';

import { MdwAlertNotificationBase } from './alert-notification-base/alert-notification-base';
import { MdwPendingAlerts } from './pending-alerts/pending-alerts';

@Injectable({ providedIn: 'root' })
export class MdwAlertNotificationsService {
  private overlay = inject(Overlay);
  private logger = inject(LoggerService);

  public pendingBottomOverlays = computed(() => this._pendingBottomOverlays());
  public pendingTopOverlays = computed(() => this._pendingTopOverlays());

  private _pendingBottomOverlays = signal<
    Array<{
      component: Type<MdwAlertNotificationBase>;
      id: string;
      inputs?: Partial<unknown>;
      autoDismissMs?: number;
    }>
  >([]);
  private _pendingTopOverlays = signal<
    Array<{
      component: Type<MdwAlertNotificationBase>;
      id: string;
      inputs?: Partial<unknown>;
      autoDismissMs?: number;
    }>
  >([]);

  private activeBottomOverlays: OverlayRef[] = [];
  private activeTopOverlays: OverlayRef[] = [];
  private renderedIds = new Set<string>();
  private overlayRefsById = new Map<string, OverlayRef>();

  private baseBottom = 20;
  private baseTop = 20;
  private baseRight = 20;
  private gap = 12;
  private pendingComponentHeight = 30;

  // This service is provided in root, so in the app it lives for the lifetime of the injector and
  // is never destroyed. In unit tests the injector IS destroyed between tests, and any timer still
  // pending would fire afterwards and call overlay.create() against the dead injector (NG0205).
  // Track pending timers, clear them on destroy, and make every scheduled callback bail if the
  // injector has gone away. No behavioural change in production.
  private destroyed = false;
  private pendingTimeouts = new Set<ReturnType<typeof setTimeout>>();

  public constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      this.pendingTimeouts.forEach(handle => clearTimeout(handle));
      this.pendingTimeouts.clear();
    });
  }

  private scheduleTimeout(callback: () => void, delayMs?: number) {
    const handle = setTimeout(() => {
      this.pendingTimeouts.delete(handle);
      if (this.destroyed) {
        return;
      }

      callback();
    }, delayMs);
    this.pendingTimeouts.add(handle);
  }

  public showBottomAlert<T extends MdwAlertNotificationBase>(
    id: string,
    component: Type<T>,
    inputs?: Partial<T>,
    autoDismissMs?: number,
  ) {
    this.logger.debug(
      `AlertNotificationsService - Showing bottom alert: id: ${id}, component: ${component.name}, inputs: ${JSON.stringify(inputs)}, autoDismissMs: ${autoDismissMs}`,
    );
    this.showAlert(id, 'bottom', component, inputs, autoDismissMs);
  }

  public showTopAlert<T extends MdwAlertNotificationBase>(
    id: string,
    component: Type<T>,
    inputs?: Partial<T>,
    autoDismissMs?: number,
  ) {
    this.logger.debug(
      `AlertNotificationsService - Showing top alert: id: ${id}, component: ${component.name}, inputs: ${JSON.stringify(inputs)}, autoDismissMs: ${autoDismissMs}`,
    );
    this.showAlert(id, 'top', component, inputs, autoDismissMs);
  }

  public closeAlert(id: string) {
    const alert = this.overlayRefsById.get(id);
    if (alert) {
      alert.dispose();
      this.overlayRefsById.delete(id);
    }

    if (this.renderedIds.has(id)) {
      this.renderedIds.delete(id);
    }
  }

  private showAlert<T extends MdwAlertNotificationBase>(
    id: string,
    position: 'bottom' | 'top',
    component: Type<T>,
    inputs?: Partial<T>,
    autoDismissMs?: number,
  ) {
    if (this.destroyed) {
      return;
    }

    if (this.renderedIds.has(id)) {
      return;
    }

    let topOffset = this.baseTop;
    let bottomOffset = this.baseBottom;
    const rightOffset = this.baseRight;
    this.logger.debug(
      `AlertNotificationsService - Showing alert: ${component.name}, inputs: ${JSON.stringify(inputs)}, autoDismissMs: ${autoDismissMs}, id: ${id}`,
    );

    const canAttach = this.canAttach(position);
    this.logger.debug('AlertNotificationsService - Can attach overlay:', canAttach);

    const existingActiveOverlays = (
      position === 'bottom' ? this.activeBottomOverlays : this.activeTopOverlays
    ).filter(o => o.overlayElement.classList.contains('active-alert'));

    this.logger.debug(
      `AlertNotificationsService - Existing active overlays for position ${position}:`,
      existingActiveOverlays,
    );

    const lastOverlay = existingActiveOverlays[existingActiveOverlays.length - 1];
    const pendingOverlays =
      position === 'bottom' ? this._pendingBottomOverlays() : this._pendingTopOverlays();

    this.logger.debug(
      `AlertNotificationsService - Pending overlays for position ${position}:`,
      pendingOverlays,
    );

    const hasPending = pendingOverlays.length > 0;
    const pendingOffset = hasPending ? this.pendingComponentHeight + this.gap : 0;

    this.logger.debug(
      `AlertNotificationsService - Pending offset for position ${position}: ${pendingOffset}`,
    );

    if (canAttach && lastOverlay) {
      if (position === 'bottom') {
        bottomOffset =
          lastOverlay.overlayElement.getBoundingClientRect().height +
          this.gap +
          this.baseBottom +
          pendingOffset;
      } else {
        topOffset =
          lastOverlay.overlayElement.getBoundingClientRect().height +
          this.gap +
          this.baseTop +
          pendingOffset;
      }
    }

    const overlayRef = this.overlay.create({
      positionStrategy:
        position === 'bottom'
          ? this.overlay.position().global().bottom(`${bottomOffset}px`).right(`${rightOffset}px`)
          : this.overlay.position().global().top(`${topOffset}px`).right(`${rightOffset}px`),
      hasBackdrop: false,
      panelClass: ['mdw-alert-notification', 'active-alert'],
    });

    const componentPortal = new ComponentPortal(component);

    if (!canAttach) {
      this.logger.debug(
        `AlertNotificationsService - Cannot attach overlay for position ${position}, adding to pending overlays.`,
      );
      this.updatePendingOverlays(id, position, component, inputs, autoDismissMs);
      return;
    }

    this.renderedIds.add(id);
    if (position === 'bottom') {
      this.activeBottomOverlays.push(overlayRef);
    } else {
      this.activeTopOverlays.push(overlayRef);
    }

    this.overlayRefsById.set(id, overlayRef);

    const componentRef = overlayRef.attach(componentPortal);

    if (inputs) {
      Object.assign(componentRef.instance as object, inputs);
    }

    componentRef.instance.dismiss.subscribe(() => overlayRef.dispose());

    overlayRef.detachments().subscribe(() => {
      this.handleDismissOverlay(id, position, overlayRef);
    });

    if (autoDismissMs) {
      this.scheduleTimeout(() => {
        overlayRef.dispose();
      }, autoDismissMs);
    }

    return componentRef;
  }

  private canAttach(position: 'bottom' | 'top') {
    const activeOverlays =
      position === 'bottom' ? this.activeBottomOverlays : this.activeTopOverlays;

    return (
      activeOverlays.filter(o => o.overlayElement.classList.contains('active-alert')).length < 2
    );
  }

  private handleDismissOverlay(id: string, position: 'bottom' | 'top', overlayRef: OverlayRef) {
    this.renderedIds.delete(id);
    this.overlayRefsById.delete(id);

    if (position === 'bottom') {
      this.activeBottomOverlays = this.activeBottomOverlays.filter(o => o !== overlayRef);
      const pending = this._pendingBottomOverlays();
      const willStillHavePending = pending.length > 1;
      const pendingOffset = willStillHavePending ? this.pendingComponentHeight + this.gap : 0;
      const nextPending = pending.shift();
      this._pendingBottomOverlays.set([...pending]);

      this.logger.debug(
        `AlertNotificationsService - Dismissing bottom overlay, remaining active overlays: ${this.activeBottomOverlays.length}`,
      );

      this.logger.debug(
        `AlertNotificationsService - renderedIds: ${Array.from(this.renderedIds).join(', ')}`,
      );

      if (this.activeBottomOverlays.length > 0) {
        const remainingOverlay = this.activeBottomOverlays[0];
        remainingOverlay.updatePositionStrategy(
          this.overlay
            .position()
            .global()
            .bottom(`${this.baseBottom + pendingOffset}px`)
            .right(`${this.baseRight}px`),
        );
      }

      if (nextPending) {
        this.scheduleTimeout(
          () =>
            this.showBottomAlert(
              nextPending.id,
              nextPending.component,
              nextPending.inputs,
              nextPending.autoDismissMs,
            ),
          300,
        );
      }
    } else {
      this.activeTopOverlays = this.activeTopOverlays.filter(o => o !== overlayRef);
      const pending = this._pendingTopOverlays();
      const willStillHavePending = pending.length > 1;
      const pendingOffset = willStillHavePending ? this.pendingComponentHeight + this.gap : 0;
      const nextPending = pending.shift();
      this._pendingTopOverlays.set([...pending]);

      if (this.activeTopOverlays.length > 0) {
        const remainingOverlay = this.activeTopOverlays[0];
        remainingOverlay.updatePositionStrategy(
          this.overlay
            .position()
            .global()
            .top(`${this.baseTop + pendingOffset}px`)
            .right(`${this.baseRight}px`),
        );
      }

      if (nextPending) {
        this.scheduleTimeout(
          () =>
            this.showTopAlert(
              nextPending.id,
              nextPending.component,
              nextPending.inputs,
              nextPending.autoDismissMs,
            ),
          300,
        );
      }
    }
  }

  private updatePendingOverlays(
    id: string,
    position: 'bottom' | 'top',
    component: Type<MdwAlertNotificationBase>,
    inputs?: Partial<unknown>,
    autoDismissMs?: number,
  ) {
    if (position === 'bottom') {
      const currentPending = this._pendingBottomOverlays();
      currentPending.push({
        id,
        component,
        inputs,
        autoDismissMs,
      });
      this._pendingBottomOverlays.set([...currentPending]);
    } else {
      const currentPending = this._pendingTopOverlays();
      currentPending.push({
        id,
        component,
        inputs,
        autoDismissMs,
      });
      this._pendingTopOverlays.set([...currentPending]);
    }

    this.updateActiveOverlaysForPending(position);
  }

  private updateActiveOverlaysForPending(position: 'bottom' | 'top') {
    const pendingOverlays =
      position === 'bottom' ? this._pendingBottomOverlays() : this._pendingTopOverlays();

    // just added the first pending overlay,
    // need to shift the position of the active overlays
    // to show the pending overlay
    if (pendingOverlays.length === 1) {
      if (position === 'bottom') {
        this.activeBottomOverlays.forEach((overlay, index) => {
          const offset =
            index > 0
              ? this.activeBottomOverlays[index - 1].overlayElement.getBoundingClientRect().height +
                this.gap
              : 0;
          overlay.updatePositionStrategy(
            this.overlay
              .position()
              .global()
              .bottom(`${this.baseBottom + offset + this.pendingComponentHeight + this.gap}px`)
              .right(`${this.baseRight}px`),
          );
        });
      } else {
        this.activeTopOverlays.forEach((overlay, index) => {
          const offset =
            index > 0
              ? this.activeTopOverlays[index - 1].overlayElement.getBoundingClientRect().height +
                this.gap * 2
              : 0;
          overlay.updatePositionStrategy(
            this.overlay
              .position()
              .global()
              .top(`${this.baseTop + offset + this.pendingComponentHeight + this.gap}px`)
              .right(`${this.baseRight}px`),
          );
        });
      }

      // add the pending overlay, but wait for the shift animation to complete
      this.scheduleTimeout(() => {
        const overlayRef = this.overlay.create({
          positionStrategy:
            position === 'bottom'
              ? this.overlay
                  .position()
                  .global()
                  .bottom(`${this.baseBottom}px`)
                  .right(`${this.baseRight}px`)
              : this.overlay
                  .position()
                  .global()
                  .top(`${this.baseTop}px`)
                  .right(`${this.baseRight}px`),
          hasBackdrop: false,
          panelClass: 'mdw-alert-notification',
        });

        const componentPortal = new ComponentPortal(MdwPendingAlerts);
        const componentRef = overlayRef.attach(componentPortal);
        componentRef.instance.isBottomPending = position === 'bottom';
        componentRef.instance.dismiss.subscribe(() => overlayRef.dispose());
      }, 300);
    }
  }
}
